import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import axios from 'axios';
import { ChevronDown, ChevronUp, MapPin } from 'lucide-react';
import { Button } from '../ui/button';

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface MapLocationSelectorProps {
  onLocationChange: (lat: number, lng: number) => void;
  initialLat?: number;
  initialLng?: number;
  street?: string;
  streetNumber?: string;
  city?: string;
  postalCode?: string;
  country?: string;
}

const LocationMarker: React.FC<{ position: [number, number] | null }> = ({ position }) => {
  return position === null ? null : (
    <Marker position={position} />
  );
};

const MapController: React.FC<{ position: [number, number] | null }> = ({ position }) => {
  const map = useMap();

  useEffect(() => {
    if (position) {
      map.setView(position, 14);
    }
  }, [position, map]);

  return null;
};

const MapLocationSelector: React.FC<MapLocationSelectorProps> = ({ 
  onLocationChange, 
  initialLat = -34.6037, 
  initialLng = -58.3816,
  street,
  streetNumber,
  city,
  postalCode,
  country
}) => {
  const [position, setPosition] = useState<[number, number] | null>(initialLat && initialLng ? [initialLat, initialLng] : null);
  const [searchAddress, setSearchAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [showManualInput, setShowManualInput] = useState(false);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-concatenar la dirección cuando cambien los campos
  useEffect(() => {
    const addressParts = [street, streetNumber, city, postalCode, country].filter(Boolean);
    const concatenatedAddress = addressParts.join(', ');
    setSearchAddress(concatenatedAddress);
  }, [street, streetNumber, city, postalCode, country]);

  // Búsqueda automática cuando se completan los campos
  useEffect(() => {
    // Limpiar timeout anterior si existe
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Solo buscar si hay al menos ciudad y país (campos mínimos para una búsqueda efectiva)
    if (city && country) {
      debounceTimeoutRef.current = setTimeout(() => {
        handleAutoSearch();
      }, 800); // Esperar 800ms después del último cambio antes de buscar
    }

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [street, streetNumber, city, postalCode, country]);

  const handleAutoSearch = async () => {
    const addressParts = [street, streetNumber, city, postalCode, country].filter(Boolean);
    if (addressParts.length < 2) return; // Necesitamos al menos 2 componentes

    const searchQuery = addressParts.join(', ');
    if (!searchQuery.trim()) return;

    try {
      const response = await axios.get(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}`);
      if (response.data && response.data.length > 0) {
        const { lat, lon } = response.data[0];
        const newPosition: [number, number] = [parseFloat(lat), parseFloat(lon)];
        setPosition(newPosition);
        onLocationChange(newPosition[0], newPosition[1]);
      }
    } catch (error) {
      console.error('Error en búsqueda automática:', error);
    }
  };

  const handleManualSearch = async () => {
    if (!searchAddress.trim()) return;

    setLoading(true);
    try {
      const response = await axios.get(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchAddress)}`);
      if (response.data && response.data.length > 0) {
        const { lat, lon } = response.data[0];
        const newPosition: [number, number] = [parseFloat(lat), parseFloat(lon)];
        setPosition(newPosition);
        onLocationChange(newPosition[0], newPosition[1]);
      } else {
        alert('No se encontró la dirección. Intenta con una dirección más específica.');
      }
    } catch (error) {
      console.error('Error al buscar la dirección:', error);
      alert('Error al buscar la dirección. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleManualSearch();
    }
  };

  return (
    <div className="space-y-4">
      {/* Mapa primero */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-foreground">Ubicación en el Mapa</label>
        <div className="relative z-0 h-64 border rounded-lg border-border">
          <MapContainer 
            center={position || [initialLat, initialLng]} 
            zoom={17} 
            style={{ height: '100%', width: '100%', borderRadius: '0.5rem' }}
            dragging={false}
            scrollWheelZoom={false}
            zoomControl={true}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            <LocationMarker position={position} />
            <MapController position={position} />
          </MapContainer>
        </div>
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            Ubicación actual: Lat: {position ? position[0].toFixed(6) : initialLat.toFixed(6)}, Lng: {position ? position[1].toFixed(6) : initialLng.toFixed(6)}
          </p>
          <Button 
            type="button"
            variant="outline" 
            className="w-full gap-2 rounded-lg"
            onClick={() => {
              window.open(`https://www.google.com/maps?q=${position ? position[0] : initialLat},${position ? position[1] : initialLng}`, '_blank');
            }}
          >
            <MapPin className="w-4 h-4" />
            Abrir en Google Maps
          </Button>
        </div>
      </div>

      {/* Botón para ingresar dirección manualmente */}
      <div className="pt-2">
        <button
          type="button"
          onClick={() => setShowManualInput(!showManualInput)}
          className="flex items-center gap-2 text-sm font-medium text-primary hover:underline focus:outline-none"
        >
          {showManualInput ? (
            <>
              <ChevronUp className="w-4 h-4" />
              Ocultar búsqueda manual
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4" />
              Ingresar dirección manualmente
            </>
          )}
        </button>
      </div>

      {/* Input de búsqueda manual (colapsable) */}
      {showManualInput && (
        <div className="p-4 space-y-3 border rounded-lg bg-muted/50 border-border">
          <label className="block text-sm font-medium text-foreground">Buscar Dirección Manualmente</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={searchAddress}
              onChange={(e) => setSearchAddress(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Edita la dirección y presiona buscar"
              className="flex-1 px-3 py-2 border rounded-lg bg-background border-border focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <button
              type="button"
              onClick={handleManualSearch}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium rounded-lg text-primary-foreground bg-primary hover:opacity-90 disabled:opacity-50"
            >
              {loading ? 'Buscando...' : 'Buscar'}
            </button>
          </div>
          <p className="text-xs text-muted-foreground">
            La dirección se actualiza automáticamente basándose en los campos de arriba. Puedes editarla aquí si necesitas ajustarla.
          </p>
        </div>
      )}
    </div>
  );
};

export default MapLocationSelector;